import axios from "axios";
import { convertLocale, getCurrentLocale } from "../../../i18n/helpers";
import { FoodType, MACRONUTRIENTS_401_ATTRS } from "../../../modules/constants";
import { parseParams } from "../../../modules/http";
import { nutritionixAcceptedLocales } from "../../../config";

export const fetchFoodAPI =
  (searchValue = "", locale) =>
  async (dispatch, getState) => {
    let currentLocale = convertLocale(locale || getCurrentLocale());

    const locales = [];

    // Get the list of accepted locales
    let acceptedLocales = [];
    try {
      // Get the list of accepted locales
      acceptedLocales = await nutritionixAcceptedLocales();
    } catch (error) {
      console.error("Failed to fetch accepted locales:", error);
      // Proceed with a default value if fetching accepted locales fails
      acceptedLocales = [];
    }

    // Change the locales to values that are accepted by Nutritionix
    if (currentLocale === "es_US" && acceptedLocales.includes("es_ES")) {
      currentLocale = "es_ES";
    } else if (currentLocale === "fr_CA" && acceptedLocales.includes("fr_FR")) {
      currentLocale = "fr_FR";
    } else if (currentLocale === "cs_CS" && acceptedLocales.includes("cs_CZ")) {
      currentLocale = "cs_CZ";
    } else if (currentLocale === "ja_JA" && acceptedLocales.includes("ja_JP")) {
      currentLocale = "ja_JP";
    }

    // Check if currentLocale is in the accepted locales
    if (acceptedLocales.includes(currentLocale)) {
      locales.push(currentLocale);
    } else {
      console.log(
        `Warning: currentLocale "${currentLocale}" is not in the list of accepted locales. Using "en_US" only.`
      );
    }

    // Always include "en_US"
    if (!locales.includes("en_US")) {
      locales.push("en_US");
    }

    return new Promise((resolve, reject) => {
      const params = {
        query: searchValue,
        Locales: locales,
      };

      return axios
        .get(`api/nutrition/search`, {
          params,
          paramsSerializer: (params) => parseParams(params),
        })
        .then((response) => {
          let processData = [];

          const { custom, branded, common } = { ...response.data };

          const customArr = custom?.map((item, i) => ({
            title: `${item.food_name}${
              item.brand_name ? `, ${item.brand_name}` : ""
            }`,
            id: i,
            type: FoodType.CUSTOM,
            item: item.customMealItemID,
          }));

          const totalCustom = custom?.length - 1;

          const commonArr = common?.map((item, i) => ({
            title: item.food_name,
            id: (i + 1) * (totalCustom || 1),
            type: FoodType.COMMON,
            item: item.food_name,
            locale: item.locale,
          }));

          const totalCommon = common?.length - 1;

          const brandedArr = branded?.map((item, i) => ({
            title: `${item.food_name}, ${item.brand_name}`,
            id: (i + 1) * (totalCommon || 1),
            type: FoodType.BRANDED,
            item: item.nix_item_id,
            locale: item.locale,
          }));

          if (customArr) {
            processData = processData.concat(customArr);
          }

          if (commonArr) {
            processData = processData.concat(commonArr);
          }

          if (brandedArr) {
            processData = processData.concat(brandedArr);
          }

          return resolve(processData);
        })
        .catch(() => {
          reject([]);
        });
    });
  };

const handleFoodDetailsResponse = (
  response,
  foodType,
  resolve,
  nutritionFields
) => {
  let processData = response?.data?.foods[0];

  const defaultMeasure = {
    serving_weight: processData?.serving_weight_grams,
    measure: processData?.serving_unit,
    seq: null,
    qty: processData?.serving_qty,
  };

  const measures = processData?.alt_measures
    ? processData?.alt_measures
    : [defaultMeasure];

  const fullNutrients =
    processData?.full_nutrients?.map((nutrientItem) => {
      return {
        attr_id: Number(nutrientItem.attr_id),
        value: Number(nutrientItem.value),
      };
    }) ?? [];

  let allNutrients = [];

  const nutrientsToFetch =
    nutritionFields?.map((element) => element.attr_id) || [];

  for (let i = 0; i < fullNutrients.length; i++) {
    if (nutrientsToFetch.includes(fullNutrients[i].attr_id)) {
      allNutrients.push(fullNutrients[i]);
    }

    if (allNutrients.length === nutrientsToFetch.length) {
      break;
    }
  }

  if (
    !allNutrients.some(
      (n) =>
        (n.attr_id === MACRONUTRIENTS_401_ATTRS.LONG_CHAIN_FATS_ATTR_ID &&
          n.value > 0) ||
        (n.attr_id === MACRONUTRIENTS_401_ATTRS.MEDIUM_CHAIN_FATS_ATTR_ID &&
          n.value > 0)
    )
  ) {
    allNutrients.push({
      attr_id: MACRONUTRIENTS_401_ATTRS.LONG_CHAIN_FATS_ATTR_ID,
      value:
        fullNutrients.find(
          (n) => n.attr_id === MACRONUTRIENTS_401_ATTRS.FATS_ATTR_ID
        )?.value ?? 0,
    });
  }

  const result = {
    title: `${processData?.food_name}${
      processData?.brand_name ? `, ${processData?.brand_name}` : ""
    }`,
    measures: measures,
    defaultMeasure: defaultMeasure,
    fullNutrients: allNutrients,
    foodType: foodType,
  };

  return resolve(result);
};

export const fetchFoodDetailsAPI =
  (item, type, locale) => (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const params = {
        Locale: locale || "en_US",
        query: item,
        foodType: type,
      };

      return axios
        .post(`api/nutrition/nutrients`, params)
        .then((response) => {
          return handleFoodDetailsResponse(
            response,
            type,
            resolve,
            getState()?.nutritionFields
          );
        })
        .catch(() => {
          reject([]);
        });
    });
  };

export const mapDispatchToProps = {
  fetchFoodAPI,
  fetchFoodDetailsAPI,
};
